import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import * as XLSX from 'xlsx';
import { InputFile } from '../../../shared/components/input/input.file';
import { SipConfigImportError, SipConfigImportResult, SipConfigImportRow } from '../../../shared/models/sip.model';
import { NotificationService } from '../../../shared/services/notification.service';
import { SipService } from '../../../shared/services/sip.service';
import { ValidationService, Validators as Vld } from '../../../shared/services/validation.service';
import { SipConfigFormComponent } from '../sip-config-form/sip-config-form.component';

@Component({
  standalone: false,
  selector: 'app-sip-config-import',
  templateUrl: './sip-config-import.component.html',
  styleUrls: ['./sip-config-import.component.scss'],
})
export class SipConfigImportComponent implements AfterViewInit {
  readonly MAX_ROWS = 2000;
  readonly MAX_FILE_SIZE = 1048576;

  @Output() finished = new EventEmitter<SipConfigImportResult>();
  @Output() closed = new EventEmitter<void>();
  @Input() fileList: FileList = null;

  accept = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
  acceptExtensions = ['xlsx'];

  @ViewChild(InputFile, { static: false }) input: InputFile;

  items: SipConfigImportRow[] = [];
  errors: SipConfigImportError[] = [];
  rowsCount = 0;
  validationInfo: { message: string; type: 'error' | 'warn' } | null = null;
  importResult: SipConfigImportResult;
  loading = false;

  private headers = {
    Name: 0,
    Host: 1,
    Port: 2,
    Prefix: 3,
    Username: 4,
    Password: 5,
    'OPTIONS ping': 6,
  };

  private requiredHeaders = ['Name', 'Host', 'Port', 'Prefix', 'Username', 'Password'];

  private propertyToHeader = {
    name: 'Name',
    host: 'Host',
    port: 'Port',
    prefix: 'Prefix',
    username: 'Username',
    password: 'Password',
    pingEnabled: 'OPTIONS ping',
  };

  private validators = {
    name: Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(255)]),
    host: Vld.compose([Vld.required, Vld.domainOrIp(true), Vld.maxLength(255)]),
    port: Vld.compose([Vld.required, Vld.positiveInteger(true), Vld.min(1), Vld.max(65535)]),
    prefix: Vld.compose([Vld.maxLength(20), Vld.sipPrefix(true)]),
    username: Vld.compose([
      Vld.minLength(3),
      Vld.maxLength(32),
      Vld.regexp(SipConfigFormComponent.USERNAME_VLD.regexp, SipConfigFormComponent.USERNAME_VLD.message, true),
    ]),
    password: Vld.compose([
      Vld.minLength(12),
      Vld.maxLength(128),
      Vld.regexp(SipConfigFormComponent.PASSWORD_VLD.regexp, SipConfigFormComponent.PASSWORD_VLD.message, true),
    ]),
  };

  constructor(
    public service: SipService,
    private validationService: ValidationService,
    private notificationService: NotificationService
  ) {}

  ngAfterViewInit() {
    if (this.fileList) {
      this.input.onChangeInput(this.fileList);
    }
  }

  onChangeImportFile(files: File[]) {
    if (files instanceof Event) {
      return;
    }
    this.resetState();
    if (!files.length) {
      return;
    }
    this.importFromExcelFile(files[0]);
  }

  importFromExcelFile(file: File) {
    const reader: FileReader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result;
      if (typeof result !== 'string') {
        return;
      }
      const workbook: XLSX.WorkBook = XLSX.read(result, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet: XLSX.WorkSheet = workbook.Sheets[sheetName];
      const rows = (XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as unknown[][]).filter(row =>
        row.some(cell => String(cell).trim() !== '')
      );

      this.rowsCount = rows.length > 1 ? rows.length - 1 : 0;
      if (!this.rowsCount) {
        this.validationInfo = {
          message: '<b>The file is empty. Please provide valid data rows.</b>',
          type: 'error',
        };
        return;
      }
      if (this.rowsCount > this.MAX_ROWS) {
        this.validationInfo = {
          message: `The file contains too many rows <b>${this.rowsCount}</b>. Please provide up to <b>${this.MAX_ROWS}</b> rows.`,
          type: 'error',
        };
        return;
      }
      this.import(rows);
    };
    reader.readAsBinaryString(file);
  }

  onSubmitImport() {
    this.loading = true;
    this.service.importConfigs(this.items).subscribe({
      next: result => {
        this.loading = false;
        this.importResult = result;
        this.errors = result.errors;
        if (result.failed) {
          this.notificationService.warning(`Import finished. SIP trunks created: ${result.created}. Failed rows: ${result.failed}.`, 'SIP config');
        } else {
          this.notificationService.success(`Import finished. SIP trunks created: ${result.created}.`, 'SIP config');
        }
        this.finished.emit(result);
      },
      error: error => {
        this.loading = false;
        this.notificationService.error({
          title: 'SIP config',
          message: 'An error occurred while importing SIP trunks',
          serviceName: 'SIP',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
      },
    });
  }

  onSubmitClose() {
    this.closed.emit();
  }

  private import(rows: unknown[][]) {
    const skippedHeaders = this.getSkippedHeaders(rows[0]);
    if (skippedHeaders.length) {
      this.errors = [
        {
          rowNumber: 1,
          messages: ['File does not contain headers: ' + skippedHeaders.join(', ')],
        },
      ];
      return;
    }

    const hasOptionsPingHeader = this.hasHeader(rows[0], 'OPTIONS ping');
    const importItems: SipConfigImportRow[] = [];
    const errors: SipConfigImportError[] = [];
    const duplicateNames = this.findDuplicateNames(rows.slice(1));

    rows.forEach((row, indexRow) => {
      if (indexRow === 0) {
        return;
      }
      const importRow = this.prepareRow(row, indexRow + 1, duplicateNames, hasOptionsPingHeader);
      if (importRow.errors.length) {
        errors.push({ rowNumber: indexRow + 1, name: importRow.row.name, messages: importRow.errors });
      } else {
        importItems.push(importRow.row);
      }
    });

    this.errors = errors;
    if (!errors.length) {
      this.items = importItems;
    }
  }

  private resetState() {
    this.items = [];
    this.errors = [];
    this.validationInfo = null;
    this.rowsCount = 0;
    this.importResult = null;
  }

  private getSkippedHeaders(columns: unknown[]): string[] {
    return this.requiredHeaders.filter(header => !this.hasHeader(columns, header));
  }

  private prepareRow(
    columns: unknown[],
    rowNumber: number,
    duplicateNames: Set<string>,
    hasOptionsPingHeader: boolean
  ): { row: SipConfigImportRow; errors: string[] } {
    const row: SipConfigImportRow = {
      rowNumber,
      name: this.getCell(columns, 'Name'),
      host: this.getCell(columns, 'Host'),
      port: this.getNumberCell(columns, 'Port'),
      prefix: this.getCell(columns, 'Prefix') || null,
      username: this.getCell(columns, 'Username') || null,
      password: this.getCell(columns, 'Password') || null,
      pingEnabled: this.getOptionsPingCell(columns, hasOptionsPingHeader),
    };
    const errors = this.validateRow(row);

    if (row.name && duplicateNames.has(row.name.toLowerCase())) {
      errors.push('Name is duplicated in the import file');
    }
    return { row, errors };
  }

  private validateRow(row: SipConfigImportRow): string[] {
    const errors = [];
    Object.keys(this.validators).forEach(prop => {
      const value = row[prop];
      const headerName = this.propertyToHeader[prop];
      this.validate(this.validators[prop], value).forEach(error => errors.push(headerName + ' ' + this.lwfirst(error)));
    });

    const hasUsername = !!row.username;
    const hasPassword = !!row.password;
    if (hasUsername && !hasPassword) {
      errors.push('Password is required when username is provided');
    }
    if (hasPassword && !hasUsername) {
      errors.push('Username is required when password is provided');
    }
    if (row.pingEnabled === null) {
      errors.push('OPTIONS ping must be yes or no');
    }

    return errors;
  }

  private validate(validator: ValidatorFn, value: string | number): string[] {
    const validationErrors = validator.call(null, { value });
    if (validationErrors === null) {
      return [];
    }
    const messages = [];
    for (const type in validationErrors) {
      this.validationService.getTextMessage(type, validationErrors[type]).forEach(message => messages.push(message));
    }
    return messages;
  }

  private getCell(row: unknown[], header: string): string {
    return String(row[this.headers[header]] ?? '').trim();
  }

  private hasHeader(row: unknown[], header: string): boolean {
    const value = this.getCell(row, header);
    return value === header || value === `${header}*`;
  }

  private getNumberCell(row: unknown[], header: string): number | null {
    const rawValue = this.getCell(row, header);
    if (!rawValue) {
      return null;
    }
    return Number(rawValue);
  }

  private getOptionsPingCell(row: unknown[], hasOptionsPingHeader: boolean): boolean | null {
    if (!hasOptionsPingHeader) {
      return true;
    }
    const rawValue = this.getCell(row, 'OPTIONS ping').toLowerCase();
    if (!rawValue) {
      return true;
    }
    if (rawValue === 'yes') {
      return true;
    }
    if (rawValue === 'no') {
      return false;
    }
    return null;
  }

  private findDuplicateNames(rows: unknown[][]): Set<string> {
    const counts = new Map<string, number>();
    rows.forEach(row => {
      const name = this.getCell(row, 'Name').toLowerCase();
      if (name) {
        counts.set(name, (counts.get(name) || 0) + 1);
      }
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([name]) => name)
    );
  }

  private lwfirst(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }
}
